/**
 * Pet Calorie Calculator — движок расчёта (браузерная версия для Shopify).
 * Подключается как обычный <script src="{{ 'pet-calorie-calculator.js' | asset_url }}">,
 * вешает объект на window.PetCalorieCalculator.
 *
 * Формула — ветеринарный стандарт NRC 2006:
 *   RER = 70 × (вес в кг) ^ 0.75
 *   MER = RER × множитель возраста × поправка на образ жизни × поправка на цель
 */
(function (global) {
  var BASE_MULTIPLIER = {
    dog: {
      puppy: 2.5,
      junior: 2.0,
      adult_intact: 1.8,
      adult_neutered: 1.6,
      senior: 1.4,
    },
    cat: {
      kitten: 2.5,
      adult_intact: 1.4,
      adult_neutered: 1.2,
      senior: 1.1,
    },
  };

  var ACTIVITY_ADJUSTMENT = {
    indoor: 0.9,
    standard: 1.0,
    active: 1.2,
  };

  // ---- Поправка на цель/состояние ----
  // lactating — очень грубое приближение (реально зависит от числа детёнышей
  // и недели лактации, разброс 2-4x) — в тексте совета явно указано
  // проконсультироваться с ветеринаром.
  var GOAL_ADJUSTMENT = {
    maintain: 1.0,
    weight_loss: 0.85,
    weight_gain: 1.15,
    lactating: 3.0,
  };

  var FALLBACK_KCAL_PER_100G = {
    dog: { dry: 350, wet: 90 },
    cat: { dry: 375, wet: 85 },
  };

  // ---- Пороги возрастных категорий в месяцах (зависят от размера породы у собак) ----
  var AGE_THRESHOLDS_MONTHS = {
    cat: { kittenEnd: 12, seniorStart: 120 },
    dog: {
      small: { puppyEnd: 9, juniorEnd: 12, seniorStart: 120 },
      medium: { puppyEnd: 10, juniorEnd: 14, seniorStart: 96 },
      large: { puppyEnd: 15, juniorEnd: 24, seniorStart: 72 },
    },
  };

  function getRER(weightKg) {
    return 70 * Math.pow(weightKg, 0.75);
  }

  function getBaseMultiplier(species, ageCategory, sterilized) {
    var table = BASE_MULTIPLIER[species];
    if (!table) throw new Error('Неизвестный species: "' + species + '"');
    if (ageCategory === 'adult') {
      return sterilized ? table.adult_neutered : table.adult_intact;
    }
    var value = table[ageCategory];
    if (!value) throw new Error('Неизвестная категория возраста "' + ageCategory + '" для species "' + species + '"');
    return value;
  }

  function getAgeCategoryFromMonths(species, months, breedSize) {
    if (species === 'cat') {
      var c = AGE_THRESHOLDS_MONTHS.cat;
      if (months < c.kittenEnd) return 'kitten';
      if (months >= c.seniorStart) return 'senior';
      return 'adult';
    }
    var t = AGE_THRESHOLDS_MONTHS.dog[breedSize || 'medium'];
    if (months < t.puppyEnd) return 'puppy';
    if (months < t.juniorEnd) return 'junior';
    if (months >= t.seniorStart) return 'senior';
    return 'adult';
  }

  function getSeniorStartMonths(species, breedSize) {
    if (species === 'cat') return AGE_THRESHOLDS_MONTHS.cat.seniorStart;
    return AGE_THRESHOLDS_MONTHS.dog[breedSize || 'medium'].seniorStart;
  }

  function getKcalPer100g({ species, foodType, providedValue }) {
    if (providedValue && providedValue > 0) return providedValue;
    return FALLBACK_KCAL_PER_100G[species] && FALLBACK_KCAL_PER_100G[species][foodType];
  }

  /**
   * @param {Object} p
   * @param {'dog'|'cat'} p.species
   * @param {number} p.weightKg
   * @param {string} p.ageCategory
   * @param {boolean} [p.sterilized]
   * @param {'indoor'|'standard'|'active'} [p.lifestyle='standard']
   * @param {'maintain'|'weight_loss'|'weight_gain'|'lactating'} [p.goal='maintain']
   * @param {'dry'|'wet'} p.foodType
   * @param {number} [p.kcalPer100g]
   * @param {number} [p.mealsPerDay=2]
   * @param {number} [p.cupWeightGrams=120]
   */
  function calculatePetNutrition(p) {
    var species = p.species;
    var weightKg = p.weightKg;
    var ageCategory = p.ageCategory;
    var sterilized = !!p.sterilized;
    var lifestyle = p.lifestyle || 'standard';
    var goal = p.goal || 'maintain';
    var foodType = p.foodType;
    var kcalPer100g = p.kcalPer100g;
    var mealsPerDay = p.mealsPerDay || 2;
    var cupWeightGrams = p.cupWeightGrams || 120;

    var rer = getRER(weightKg);
    var baseMultiplier = getBaseMultiplier(species, ageCategory, sterilized);
    var activityAdjustment = ACTIVITY_ADJUSTMENT[lifestyle] || 1.0;
    var goalAdjustment = GOAL_ADJUSTMENT[goal] || 1.0;
    var mer = rer * baseMultiplier * activityAdjustment * goalAdjustment;

    var usedFallbackKcal = !(kcalPer100g && kcalPer100g > 0);
    var resolvedKcal = getKcalPer100g({ species: species, foodType: foodType, providedValue: kcalPer100g });

    var gramsPerDay = resolvedKcal ? (mer / resolvedKcal) * 100 : null;
    var gramsPerMeal = gramsPerDay ? gramsPerDay / mealsPerDay : null;
    var cupsPerDay = (foodType === 'dry' && gramsPerDay)
      ? Math.round((gramsPerDay / cupWeightGrams) * 10) / 10
      : null;

    return {
      rer: Math.round(rer),
      mer: Math.round(mer),
      kcalPer100gUsed: resolvedKcal,
      usedFallbackKcal: usedFallbackKcal,
      gramsPerDay: gramsPerDay ? Math.round(gramsPerDay) : null,
      gramsPerMeal: gramsPerMeal ? Math.round(gramsPerMeal) : null,
      cupsPerDay: cupsPerDay,
      mealsPerDay: mealsPerDay,
    };
  }

  global.PetCalorieCalculator = {
    calculatePetNutrition: calculatePetNutrition,
    getAgeCategoryFromMonths: getAgeCategoryFromMonths,
    getSeniorStartMonths: getSeniorStartMonths,
    BASE_MULTIPLIER: BASE_MULTIPLIER,
    ACTIVITY_ADJUSTMENT: ACTIVITY_ADJUSTMENT,
    GOAL_ADJUSTMENT: GOAL_ADJUSTMENT,
    FALLBACK_KCAL_PER_100G: FALLBACK_KCAL_PER_100G,
    AGE_THRESHOLDS_MONTHS: AGE_THRESHOLDS_MONTHS,
  };
})(window);